import _ from 'lodash';
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { hexToRgb } from '@scale/frontend-lib/colors';
import { getAllChoiceDescendants } from '@scale/shared/annotation';
import { IInstructionListJson, IInstructionItemJson } from '@scale/shared/types/instructionitem';
import { LabelChoice, getLabelChoiceValue } from '@scale/shared/types/params/annotation';
import AttributeRadioInput from './AttributeRadioInput';

interface AttributeRadioTreeProps {
  tree: LabelChoice;
  instructionList?: IInstructionListJson;
  isReadOnly?: boolean;
  selectedChoice?: string;
  handleSelect?: (choice: string) => void;
  colorMapping?: Record<string, string>;
}

interface AttributeRadioTreeState {
  expanded: boolean;
}

export default class AttributeRadioTree extends React.Component<
  AttributeRadioTreeProps,
  AttributeRadioTreeState
> {
  state: AttributeRadioTreeState = {
    expanded: false,
  };

  componentDidMount() {
    this.expandToSelectedDescendant();
  }

  // originally componentWillReceiveProps
  componentDidUpdate(prevProps: AttributeRadioTreeProps) {
    if (prevProps.selectedChoice !== this.props.selectedChoice) {
      this.expandToSelectedDescendant();
    }
  }

  expandToSelectedDescendant() {
    if (this.state.expanded) {
      return;
    }

    let subchoices: LabelChoice[];
    if (_.isString(this.props.tree)) {
      subchoices = [];
    } else {
      subchoices = this.props.tree.subchoices || [];
    }
    const descendants = getAllChoiceDescendants(subchoices);

    if (_.includes(descendants, this.props.selectedChoice)) {
      this.setState({ expanded: true });
    }
  }

  handleExpand = () => {
    this.setState({
      expanded: true,
    });
  };

  handleToggleExpanded = () => {
    this.setState(prevState => {
      return {
        expanded: !prevState.expanded,
      };
    });
  };

  render() {
    const categoryName = _.isString(this.props.tree) ? this.props.tree : this.props.tree.choice;
    let categoryDisplayName: string;
    let categoryChildren: LabelChoice[];
    if (_.isString(this.props.tree)) {
      categoryDisplayName = this.props.tree;
      categoryChildren = [];
    } else {
      categoryDisplayName = this.props.tree.display || this.props.tree.choice;
      categoryChildren = this.props.tree.subchoices || [];
    }

    const findLabelDescription = (labelName: string): string | undefined => {
      const instructionItems: IInstructionItemJson[] = _.get(
        this.props.instructionList,
        'instructionSet',
        [],
      );
      const labelInstructionItems: IInstructionItemJson[] = _.filter<IInstructionItemJson>(
        instructionItems,
        (instructionItem: IInstructionItemJson): boolean => {
          return (
            !!instructionItem.isLabelDescription &&
            _.includes(instructionItem.labels, labelName) &&
            instructionItem.enabled !== false
          );
        },
      );
      // Get most specific instruction item, in case an instruction item applies to multiple labels
      const labelInstructionItem = _.reduce(
        labelInstructionItems,
        (mostSpecificInstructionItem, instructionItem) => {
          return (mostSpecificInstructionItem.labels || []).length >
            (instructionItem.labels || []).length
            ? instructionItem
            : mostSpecificInstructionItem;
        },
      );
      return _.get(labelInstructionItem, 'text');
    };

    if (_.isEmpty(categoryChildren)) {
      return (
        <AttributeRadioInput
          choice={this.props.tree}
          selectedChoice={this.props.selectedChoice}
          isReadOnly={this.props.isReadOnly}
          handleSelect={this.props.handleSelect}
          legendColor={this.props.colorMapping && hexToRgb(this.props.colorMapping[categoryName])}
          instructionItem={findLabelDescription(categoryName)}
        />
      );
    }

    return (
      <div className="radio-selector__tree">
        <div className="radio-selector__heading" onClick={this.handleToggleExpanded}>
          <div>
            {!this.state.expanded && (
              <FontAwesomeIcon
                icon="chevron-right"
                style={{ marginRight: '10px', fontSize: '14px' }}
              />
            )}
          </div>
          <div>
            {this.state.expanded && (
              <FontAwesomeIcon
                icon="chevron-down"
                style={{ marginRight: '10px', fontSize: '14px' }}
              />
            )}
          </div>
          {categoryDisplayName}
        </div>

        <div className="radio-selector__leaves">
          {this.state.expanded &&
            categoryChildren.map(c => (
              <AttributeRadioTree
                key={getLabelChoiceValue(c)}
                tree={c}
                selectedChoice={this.props.selectedChoice}
                isReadOnly={this.props.isReadOnly}
                handleSelect={this.props.handleSelect}
                instructionList={this.props.instructionList}
              />
            ))}
        </div>

        <style jsx>{`
          .radio-selector__tree {
            padding-left: 8px;
            margin-bottom: 4px;
          }

          .radio-selector__heading {
            padding: 4px 0;
            margin-bottom: 4px;
            cursor: pointer;
            color: rgba(0, 0, 0, 0.7);
            transition: color 0.3s;
          }

          .radio-selector__heading:hover {
            color: rgba(0, 0, 0, 1);
          }

          .radio-selector__leaves {
            padding-left: 8px;
          }
        `}</style>
      </div>
    );
  }
}
